import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import AuthService from 'cio/services/auth';
import OAuthService from 'cio/services/oauth';
import { NO_OAUTH_RESPONSE, Provider } from 'cio/utils/oauth';
import ApiErrorsService from './api-errors';
import ApiService from './api';
import { REQUIRED_GOOGLE_SHEETS_SCOPE } from 'cio/utils/google-sheets';
import AlertsService from './alerts';

export default class GoogleSheetsService extends Service {
  @service auth!: AuthService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service oauth!: OAuthService;
  @service alerts!: AlertsService;

  @tracked accessToken?: string;
  // Assume the user has the correct scopes
  @tracked hasRequiredScopes = true;
  @tracked hasAuthorizedGoogleDrive = true;

  get googleCredential() {
    return this.auth.user?.googleDriveCredential;
  }

  get needToGrantAdditionalScopes() {
    return this.googleCredential != null && !this.hasRequiredScopes;
  }

  get needToReauthorize() {
    return this.googleCredential != null && !this.hasAuthorizedGoogleDrive;
  }

  deleteCredential = dropTask(async () => {
    if (this.auth.user == null) {
      this.alerts.error(
        'google-sheets-service.delete-credential.user-missing',
        'Unable to find current user. Refresh the page and try again.',
      );
      return;
    }

    await this.auth.user.deleteGoogleDriveCredential({});
  });

  getUserAccessToken = dropTask(async () => {
    try {
      if (this.auth.user == null) {
        throw new Error('Unable to find current user. Refresh the page and try again.');
      }

      this.accessToken = await this.auth.user.getGoogleDriveAccessToken({});
    } catch {
      // Generally in this case, the refresh token stored on the back end has been invalidated
      this.hasRequiredScopes = false;
      this.hasAuthorizedGoogleDrive = false;
    }
  });

  checkTokenScopes = dropTask(async () => {
    try {
      let res = await fetch(`https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${this.accessToken}`);
      let { scope } = await res.json();

      this.hasRequiredScopes = scope.includes(REQUIRED_GOOGLE_SHEETS_SCOPE);
      this.hasAuthorizedGoogleDrive = true;
    } catch {
      // In this case, unless Google is down, the token is totally invalid and
      // they need to log in again
      this.hasAuthorizedGoogleDrive = false;
    }
  });

  connectGoogle = dropTask(async () => {
    try {
      let additionalGrant = this.needToGrantAdditionalScopes && !this.needToReauthorize;

      let response = await this.oauth.open(Provider.GoogleSheets, {
        additionalGrant,
      });

      if (this.auth.user == null) {
        throw new Error('Unable to find current user. Refresh the page and try again.');
      }

      await this.auth.user.createGoogleDriveCredential({
        credential: {
          ...response,
        },
      });

      this.hasAuthorizedGoogleDrive = true;

      if (additionalGrant) {
        await this.getUserAccessToken.perform();
        await this.checkTokenScopes.perform();
      } else {
        // Assume that they gave the correct permissions the first time
        this.hasRequiredScopes = true;
      }
    } catch (err) {
      // If the user cancelled the signup process, we don't want to report an error
      // google/oauth for some reason returns an error "unknown"
      if (err.message !== NO_OAUTH_RESPONSE && err !== 'unknown') {
        this.apiErrors.report(err);
      }

      throw err;
    }
  });

  fetchSheets = dropTask(async (spreadsheetId: string) => {
    let { sheets } = await this.api.request<{ sheets: { id: number; name: string }[] }>(
      `spreadsheets/${spreadsheetId}/sheets`,
      {
        pathScope: 'global',
      },
    );

    return sheets.map((sheet) => ({ label: sheet.name, value: sheet.id.toString() }));
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'google-sheets': GoogleSheetsService;
  }
}
